import { ProgressStepper } from '@zillow/constellation-10';
import {
  SatelliteRenterProgressBarStepStatusType,
  type SatelliteWebLandlordLeadManagementRenterProgressBarStepsDto,
  type SatelliteWebLandlordLeadManagementTableRowResponseDto,
} from '@zg-rentals/ts-satellite-client';

type StepKey = keyof SatelliteWebLandlordLeadManagementRenterProgressBarStepsDto;

const STEP_DISPLAY_ORDER: Array<StepKey> = ['inquiry', 'tour', 'application', 'approved', 'lease'];

type StepTone = 'complete' | 'incomplete' | undefined;

type RenterDetailsProgressBarProps = {
  lead: SatelliteWebLandlordLeadManagementTableRowResponseDto | null;
};

type StepProps = {
  label: string;
  tone?: StepTone;
};

const getStepTone = (statusType: SatelliteRenterProgressBarStepStatusType, isPast: boolean): StepTone => {
  if (statusType === SatelliteRenterProgressBarStepStatusType.CHECKED) return 'complete';
  if (isPast) return 'incomplete';
  return undefined;
};

export default function RenterDetailsProgressBar({ lead }: RenterDetailsProgressBarProps) {
  const { renterProgressBar } = lead || {};

  if (!renterProgressBar) return null;

  const enabledStepKeys = STEP_DISPLAY_ORDER.filter((key) => renterProgressBar[key].isEnabled);

  // To show a step as a checkmark (tone='complete'), activeStepIndex must be past it.
  const activeStepIndex = (() => {
    // All steps complete — advance past the end so every step renders its tone.
    if (renterProgressBar.lease.statusType === SatelliteRenterProgressBarStepStatusType.CHECKED) {
      return enabledStepKeys.length;
    }
    // hasStatusLabel is used as our indicator for the "current" step.
    const statusLabelIdx = enabledStepKeys.findIndex((key) => renterProgressBar[key].hasStatusLabel);
    if (statusLabelIdx >= 0) {
      return statusLabelIdx;
    }
    // Fallback: advance one past the last checked step.
    const lastCheckedIdx = enabledStepKeys.reduce(
      (last, key, idx) =>
        renterProgressBar[key].statusType === SatelliteRenterProgressBarStepStatusType.CHECKED ? idx : last,
      -1,
    );
    return lastCheckedIdx + 1;
  })();

  const steps: Array<StepProps> = enabledStepKeys.map((key, stepIndex) => {
    const { statusType } = renterProgressBar[key];
    const label = key.charAt(0).toUpperCase() + key.slice(1);
    const tone = getStepTone(statusType, stepIndex < activeStepIndex);

    return { label, tone };
  });

  return <ProgressStepper activeStepIndex={activeStepIndex} steps={steps} css={{ paddingBottom: 'default' }} />;
}
